<template>
    <div
        key="welcome"
        class="flex flex-col overflow-x-hidden flex-grow bg-main-bg-500 dark:bg-main-bg-dark-500"
    >
        <header
            class="flex px-3 py-1 justify-between shadow-app-vs-light dark:shadow-app-vs-dark"
        >
            <div class="flex items-center">
                <router-link to="/">
                    <paperback-logo
                        class="h-11 active:opacity-75 transition-opacity duration-300 ease-in-out"
                    />
                </router-link>
                <router-link to="/">
                    <h3
                        class="text-brand-500 ml-4 text-xl font-bold font-title active:opacity-75 transition-opacity duration-300 ease-in-out"
                    >
                        Paperback
                    </h3>
                </router-link>
            </div>
            <color-mode-switcher
                class="text-app-font-grey-500 dark:text-app-font-grey-dark-500 hover:text-app-font-grey-600 dark:hover:text-app-font-grey-dark-600 focus:text-app-font-grey-600 dark:focus:text-app-font-grey-dark-600 active:text-app-font-grey-400 transition-colors duration-300 ease-in-out"
            />
        </header>
        <section
            class="flex h-full px-12 md:px-16 lg:px-28 2xl:px-48 pb-12 lg:pb-24 bg-main-bg-500 dark:bg-main-bg-dark-500 justify-center items-center"
        >
            <div
                class="flex flex-shrink-0 h-full flex-col justify-center items-center"
            >
                <div class="flex flex-col w-full md:flex-row md:my-7">
                    <paperback-logo
                        class="w-16 self-center my-7 md:w-16 2xl:w-24 md:mr-6 2xl:mr-8 md:my-0"
                    />
                    <div class="flex flex-col my-7 md:my-0 justify-between">
                        <h1
                            class="font-title font-semibold text-3xl md:text-5xl 2xl:text-7xl"
                        >
                            Welcome
                        </h1>
                        <h2
                            class="font-title font-semibold text-2xl md:text-4xl 2xl:text-5xl"
                        >
                            To
                            <span class="text-brand-500">Paperback</span>
                            Portal!
                        </h2>
                    </div>
                </div>
                <router-link
                    v-if="oidcIsAuthenticated"
                    to="home"
                    class="flex w-full md:w-60"
                >
                    <button
                        class="w-full h-12 my-7 text-base text-semibold text-app-font-dark-500 bg-brand-500 hover:bg-brand-400 focus:outline-none focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-brand-200 active:bg-brand-600 rounded-md shadow-app-md-light focus:shadow-app-sm-light dark:shadow-app-md-dark dark:focus:shadow-app-sm-dark transition-colors duration-300 ease-in-out"
                    >
                        Enter
                    </button>
                </router-link>
                <div
                    v-else
                    class="flex flex-col w-full md:flex-row space-y-5 md:space-y-0 md:space-x-3 2xl:space-x-5 my-7"
                >
                    <a
                        href
                        @click.prevent="authenticateOidcPopup"
                        class="flex w-full"
                    >
                        <button
                            class="w-full h-12 text-base text-semibold text-app-font-dark-500 bg-brand-500 hover:bg-brand-400 focus:outline-none focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-brand-300 active:bg-brand-600 rounded-md shadow-app-md-light focus:shadow-app-sm-light dark:shadow-app-md-dark dark:focus:shadow-app-sm-dark transition-colors duration-300 ease-in-out"
                        >
                            Sign in
                        </button></a
                    >
                    <a :href="signUpLink" class="flex w-full">
                        <button
                            class="w-full h-12 text-base text-semibold text-app-font-light-500 bg-item-bg-500 dark:bg-item-bg-dark-400 hover:bg-item-bg-600 dark:hover:bg-item-bg-dark-500 focus:outline-none focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-item-bg-700 dark:focus-visible:ring-item-bg-dark-100 active:bg-item-bg-700 dark:active:bg-item-bg-dark-600 rounded-md shadow-app-md-light focus:shadow-app-sm-light dark:shadow-app-md-dark dark:focus:shadow-app-sm-dark transition-colors duration-300 ease-in-out"
                        >
                            Sign up
                        </button></a
                    >
                </div>
            </div>
            <div
                v-if="colorMode === 'light'"
                class="flex h-full justify-center items-center"
            >
                <img
                    src="@/assets/images/iphone-light.png"
                    alt="iPhone app preview"
                    class="hidden md:block relative z-40 md:max-w-[85%] lg:max-w-[65%] md:left-[40%] lg:left-[30%] 2xl:left-[20%] bottom-[-21%]"
                />
                <img
                    src="@/assets/images/ipad-light.png"
                    alt="iPad app preview"
                    class="hidden md:block relative md:max-w-[105%] md:right-[23%] lg:max-w-[90%] lg:right-[8%]"
                />
            </div>
            <div v-else class="flex h-full justify-center items-center">
                <img
                    src="@/assets/images/iphone-dark.png"
                    alt="iPhone app preview"
                    class="hidden md:block relative z-40 md:max-w-[85%] lg:max-w-[65%] md:left-[40%] lg:left-[30%] 2xl:left-[20%] bottom-[-21%]"
                />
                <img
                    src="@/assets/images/ipad-dark.png"
                    alt="iPad app preview"
                    class="hidden md:block relative md:max-w-[105%] md:right-[23%] lg:max-w-[90%] lg:right-[8%]"
                />
            </div>
        </section>
    </div>
</template>

<script lang="ts">
import { defineComponent, computed, onUpdated } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import PaperbackLogo from '@/common/components/ui/PaperbackLogo.vue'
import ColorModeSwitcher from '@/common/components/ui/ColorModeSwitcher.vue'
import { appRouteNames } from '@/router'
import { signUpLink } from '@/services/auth/config'

export default defineComponent({
    name: 'Welcome',
    components: {
        PaperbackLogo,
        ColorModeSwitcher,
    },
    setup() {
        const store = useStore()
        const oidcIsAuthenticated = computed(
            () => store.getters['auth/oidcIsAuthenticated']
        )
        const authenticateOidcPopup = () =>
            store.dispatch('auth/authenticateOidcPopup')
        const authenticateOidc = () => store.dispatch('auth/authenticateOidc')

        const colorMode = computed(() => store.getters['ui/colorMode'])

        onUpdated(() => {
            const router = useRouter()
            if (oidcIsAuthenticated.value) {
                router.push({ name: appRouteNames.SETTINGS })
            }
        })

        return {
            colorMode,
            oidcIsAuthenticated,
            authenticateOidcPopup,
            authenticateOidc,
            signUpLink,
        }
    },
})
</script>
